/**
 * 「业务能力/业务模块未授权」错误识别与友好文案（纯函数，无 UI 依赖）
 *
 * 后端门控拦截时返回 403，detail（或统一错误格式的 error_message）有两种契约：
 * - 模块级路径（require_module，2026-10 迁移后现行）：detail 含「业务模块」，
 *   冒号后为「 / 」分隔的模块键列表（如「未授权以下业务模块之一: tcm」）；
 * - 细码路径（require_capability 时期遗留）：detail 含「业务能力」及能力代码
 *   （如 outpatient.record_query），保留兼容。
 * 用户可见文案必须是业务名称，不得出现能力代码/模块键或 HTTP 状态码。
 */

/** capability code 前缀 → 用户可见的业务模块名称（旧「业务能力」detail 兼容路径） */
const CAPABILITY_MODULE_NAMES: Record<string, string> = {
  outpatient: "门诊综合管理",
  clinical_care: "诊断治疗",
  tcm_reasoning: "理法方药",
  medical_document: "病历书写",
  record_check: "病历质控",
  health_management: "健康管理",
  chronic_care: "慢病管理",
  workspace: "工作台",
  diagnosis: "诊断",
  clinical_scene: "急诊/住院场景",
  pharmacy: "药房",
  cdss: "诊疗辅助",
  medical_order: "医嘱模块",
  teach: "教学模块",
};

/** 模块键 → 用户可见的业务名称（与 manifest_repository._MODULES 中文名对齐） */
const MODULE_NAMES: Record<string, string> = {
  outpatient: "门诊综合管理",
  clinical: "诊断治疗",
  tcm: "理法方药",
  record: "病历书写",
  quality: "病历质控",
  record_assistant: "病历智能助手",
  cdss: "临床辅助决策",
  health: "健康管理",
  chronic: "慢病管理",
  workspace: "工作台",
  diagnosis: "诊断",
  medical_order: "医嘱",
  emergency: "急诊",
  outpatient_pharmacy: "药房",
  outpatient_charging: "门诊收费",
  inpatient_charging: "住院收费",
  inpatient_pharmacy: "住院药房",
  health_management: "健康管理",
  emr_front_sheet: "病案首页",
  teach: "教学",
};

const ENTITLEMENT_DETAIL_MARKS = ["业务能力", "业务模块"] as const;

interface ApiErrorLike {
  response?: {
    status?: number;
    data?: { detail?: unknown; error_message?: unknown };
  };
  status?: number;
}

function extractDetailText(error: unknown): string {
  const value = error as ApiErrorLike;
  const data = value?.response?.data;
  // 兼容两种错误契约：FastAPI HTTPException 的 detail 与全局统一错误的 error_message
  const detail = data?.detail ?? data?.error_message;
  return typeof detail === "string" ? detail : "";
}

/** 模块级 detail → 模块键列表（「未授权以下业务模块之一: tcm / record」冒号后段）。 */
function extractModuleKeys(detail: string): string[] {
  const idx = detail.indexOf(":");
  if (idx < 0) return [];
  return detail
    .slice(idx + 1)
    .split("/")
    .map((part) => part.trim())
    .filter((key) => /^[a-z][a-z0-9_]*$/.test(key));
}

/**
 * 若为「业务能力/业务模块未授权」403，返回友好提示文案；否则返回 null。
 * 文案把能力代码/模块键翻译为业务名称，不暴露代码和状态码。
 */
export function getEntitlementDenialMessage(error: unknown): string | null {
  const value = error as ApiErrorLike;
  const status = value?.response?.status ?? value?.status;
  if (status !== 403) return null;
  const detail = extractDetailText(error);
  if (!ENTITLEMENT_DETAIL_MARKS.some((mark) => detail.includes(mark))) {
    return null;
  }

  let names: string[];
  if (detail.includes("业务模块")) {
    names = [
      ...new Set(
        extractModuleKeys(detail)
          .map((key) => MODULE_NAMES[key])
          .filter(Boolean),
      ),
    ];
  } else {
    const codes = detail.match(/[a-z][a-z0-9_]*\.[a-z][a-z0-9_]*/g) ?? [];
    names = [
      ...new Set(
        codes
          .map((code) => CAPABILITY_MODULE_NAMES[code.split(".")[0]])
          .filter(Boolean),
      ),
    ];
  }
  const feature = names.length ? names.join("、") : "该功能";
  return `当前机构未开通「${feature}」，请联系管理员授权`;
}
